import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetNotificationSettingFieldsQuery } from '@/middlewares/defaultApi';
import type { RootState } from '@/reducers/reducers';
import { selectAppSettings } from '@/selectors/selectors';
import type { SettingField } from '@/types';

// Fields are keyed by notification id; notifications with nothing beyond channels and mentions have none
export const useNotificationSettingFields = (
    orgId: string,
    appId: string,
): {
    settingFieldsByNotificationId: Record<string, SettingField[]> | undefined;
    isLoadingSettingFields: boolean;
    isSettingFieldsError: boolean;
} => {
    const { data, isFetching, isError, refetch } = useGetNotificationSettingFieldsQuery(
        { orgId, appId },
        { refetchOnMountOrArgChange: false },
    );

    // Options and availability depend on the app's integrations, so refetch when they change
    const integrations = useSelector((state: RootState) => selectAppSettings(state, appId)?.integrations);
    const previousIntegrations = useRef(integrations);
    useEffect(() => {
        if (isEqual(previousIntegrations.current, integrations)) {
            return;
        }
        previousIntegrations.current = integrations;
        refetch();
    }, [integrations, refetch]);

    return {
        settingFieldsByNotificationId: data?.data,
        isLoadingSettingFields: isFetching,
        isSettingFieldsError: isError,
    };
};
